import { Location } from '@angular/common';
import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { AwscontrolService, UploadResponse } from 'app/services/aws/awscontrol.service';

import { SousBien } from 'app/models/sousBien';
import { BiensImmobsBiensImmobs } from 'app/services/biens-immobs/biens-immobs.service';
import { SousBienService } from 'app/services/sousBiens/sous-bien.service';

@Component({
  selector: 'app-ajouter-sous-biens',
  templateUrl: './ajouter-sous-biens.component.html',
  styleUrls: ['./ajouter-sous-biens.component.scss']
})
export class AjouterSousBiensComponent implements OnInit {
  @ViewChild('descriptionTextarea', { static: false }) descriptionTextarea!: ElementRef;

  public sousBiensadd: any;
  public bien: any;
  public idBien: any;          // Parent bien ID (from route)
  public agencyId: string | null = null; // Agency ID from the bien
  public sousBien: SousBien;
  public selectedFiles: FileList | null = null;
  public fileNames: string[] = [];
  public isUploading: boolean = false;
  public uploadProgress: number = 0;

  // Images
  public uploadedImagesList: string[] = [];
  public uploadedSingleImageUrl: string = '';

  // Loading states
  public isLoadingBien: boolean = false;
  public isSubmitting: boolean = false;

  constructor(
    private bienService: BiensImmobsBiensImmobs,
    private sousBienService: SousBienService,
    private awsService: AwscontrolService,
    private router: Router,
    private _location: Location,
    private activatedRoute: ActivatedRoute
  ) {
    this.sousBien = new SousBien();
    this.sousBien.description = '';
  }

  ngOnInit(): void {
    this.getData();
  }

  getData(): void {
    this.idBien = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.idBien) {
      this.isLoadingBien = true;
      // Use 'any' to avoid TypeScript errors with dynamic properties
      this.bienService.getBiensImmobs(this.idBien).subscribe(
        (data: any) => {
          console.log("Fetched Bien data:", data);
          this.bien = data;
          
          // Extract agency ID from the bien (data may have user, userId, or agencyId)
          if (data.user?.id) {
            this.agencyId = data.user.id.toString();
          } else if (data.userId) {
            this.agencyId = data.userId.toString();
          } else if (data.agencyId) {
            this.agencyId = data.agencyId.toString();
          }
          
          this.isLoadingBien = false;
        },
        error => {
          console.error("Error fetching Bien data:", error);
          this.isLoadingBien = false;
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Impossible de charger les données du bien.",
            confirmButtonColor: '#f44336'
          });
        }
      );
    }
  }

  // Formatage du texte dans la description
  formatText(command: string): void {
    if (!this.descriptionTextarea) return;
    
    const textarea = this.descriptionTextarea.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.sousBien.description.substring(start, end);
    
    if (selectedText.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sélectionnez du texte',
        text: 'Veuillez sélectionner le texte que vous souhaitez formater.',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }
    
    let formattedText = '';
    switch (command) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
    }
    
    const newText = this.sousBien.description.substring(0, start) + 
                   formattedText + 
                   this.sousBien.description.substring(end);
    
    this.sousBien.description = newText;
    
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + formattedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  }

  // Génération de chaîne aléatoire pour les noms de fichiers
  public getRandomString(length: number): string {
    const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
    }
    return result;
  }

  // Validation de fichier image
  private validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!validTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: `Le fichier ${file.name} n'est pas une image valide. Formats acceptés: JPG, PNG, GIF, WebP.` 
      };
    }
    
    if (file.size > 5 * 1024 * 1024) {
      return { 
        valid: false, 
        error: `L'image ${file.name} dépasse 5MB. Taille maximum: 5MB.` 
      };
    }
    
    return { valid: true };
  }

  // Upload multiple images
  selectFileimg1(e: any): void {
    this.selectedFiles = e.target.files;
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;

    const totalImages = this.uploadedImagesList.length + this.selectedFiles.length;
    if (totalImages > 30) {
      Swal.fire({
        icon: 'warning',
        title: 'Limite atteinte',
        text: `Maximum 30 images autorisées. Vous avez déjà ${this.uploadedImagesList.length} image(s).`,
        confirmButtonColor: '#f44336'
      });
      e.target.value = '';
      return;
    }

    const filesToUpload: File[] = [];
    for (let i = 0; i < this.selectedFiles.length; i++) {
      const file = this.selectedFiles[i];
      const validation = this.validateImageFile(file);
      
      if (!validation.valid) {
        Swal.fire({
          icon: 'warning',
          title: 'Fichier invalide',
          text: validation.error,
          confirmButtonColor: '#f44336'
        });
        continue;
      }
      filesToUpload.push(file);
    }

    if (filesToUpload.length > 0) {
      this.uploadMultipleImages(filesToUpload);
    }
    
    e.target.value = '';
  }

  uploadMultipleImages(files: File[]): void {
    this.isUploading = true;
    
    const renamedFiles = files.map(file => {
      const newName = `sous-bien-images/${this.idBien}/${this.getRandomString(10)}_${file.name}`;
      return new File([file], newName, { type: file.type });
    });

    this.awsService.uploadFiles(renamedFiles, `sous-bien-images/${this.idBien}`).subscribe({
      next: (response: UploadResponse) => {
        this.isUploading = false;
        
        if (response.success) {
          this.uploadedImagesList = [...this.uploadedImagesList, ...response.urls];
          
          Swal.fire({
            icon: "success",
            title: "Images ajoutées",
            text: `${response.count} image(s) uploadée(s) avec succès`,
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Échec de l'upload",
            text: response.error || "Les images n'ont pas pu être uploadées.",
            confirmButtonColor: '#f44336'
          });
        }
      },
      error: (err: any) => {
        this.isUploading = false;
        console.error('Error uploading images:', err);
        Swal.fire({
          icon: "error",
          title: "Erreur de téléchargement",
          text: "Échec du téléchargement des images. Veuillez réessayer.",
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  // Upload image principale
  selectFileimg(e: any): void {
    this.selectedFiles = e.target.files;
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;

    const file = this.selectedFiles[0];
    const validation = this.validateImageFile(file);
    
    if (!validation.valid) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier invalide',
        text: validation.error,
        confirmButtonColor: '#f44336'
      });
      e.target.value = '';
      return;
    }
    
    this.uploadFileimgPromo(file);
    e.target.value = '';
  }

  uploadFileimgPromo(file: File): void {
    this.isUploading = true;

    const renamedFile = new File([file], `sous-bien-main/${this.idBien}/${this.getRandomString(10)}_${file.name}`, { 
      type: file.type 
    });

    this.awsService.uploadFile(renamedFile, `sous-bien-main/${this.idBien}`).subscribe({
      next: (response: UploadResponse) => {
        this.isUploading = false;
        
        if (response.success && response.urls.length > 0) {
          this.uploadedSingleImageUrl = response.urls[0];
          
          Swal.fire({
            icon: "success",
            title: "Image principale ajoutée",
            text: `${file.name} a été uploadée avec succès`,
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Échec de l'upload",
            text: response.error || `Échec du téléchargement de ${file.name}.`,
            confirmButtonColor: '#f44336'
          });
        }
      },
      error: (err: any) => {
        this.isUploading = false;
        console.error('Error uploading main image:', err);
        Swal.fire({
          icon: "error",
          title: "Erreur de téléchargement",
          text: `Échec du téléchargement de ${file.name}. Veuillez réessayer.`,
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  // Gestion des images
  removeImage(index: number): void {
    const imageUrl = this.uploadedImagesList[index];
    
    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer cette image?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(imageUrl).subscribe({
          next: (response) => {
            if (response.status === 'success') {
              this.uploadedImagesList.splice(index, 1);
              Swal.fire({
                icon: "success",
                title: "Image supprimée",
                text: "L'image a été supprimée avec succès",
                timer: 1500,
                showConfirmButton: false
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Erreur",
                text: response.error || "Échec de la suppression de l'image",
                confirmButtonColor: '#f44336'
              });
            }
          },
          error: (err: any) => {
            console.error('Error deleting image:', err);
            Swal.fire({
              icon: "error",
              title: "Erreur",
              text: "Échec de la suppression de l'image. Veuillez réessayer.",
              confirmButtonColor: '#f44336'
            });
          }
        });
      }
    });
  }

  removeMainImage(): void {
    if (!this.uploadedSingleImageUrl) return;
    
    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer l'image principale?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(this.uploadedSingleImageUrl).subscribe({
          next: (response) => {
            if (response.status === 'success') {
              this.uploadedSingleImageUrl = '';
              Swal.fire({
                icon: "success",
                title: "Image supprimée",
                text: "L'image principale a été supprimée avec succès",
                timer: 1500,
                showConfirmButton: false
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Erreur",
                text: response.error || "Échec de la suppression de l'image",
                confirmButtonColor: '#f44336'
              });
            }
          },
          error: (err: any) => {
            console.error('Error deleting main image:', err);
            Swal.fire({
              icon: "error",
              title: "Erreur",
              text: "Échec de la suppression de l'image. Veuillez réessayer.",
              confirmButtonColor: '#f44336'
            });
          }
        });
      }
    });
  }

  // Tri des images
  moveImageUp(index: number): void {
    if (index > 0) {
      const temp = this.uploadedImagesList[index];
      this.uploadedImagesList[index] = this.uploadedImagesList[index - 1];
      this.uploadedImagesList[index - 1] = temp;
    }
  }

  moveImageDown(index: number): void {
    if (index < this.uploadedImagesList.length - 1) {
      const temp = this.uploadedImagesList[index];
      this.uploadedImagesList[index] = this.uploadedImagesList[index + 1];
      this.uploadedImagesList[index + 1] = temp;
    }
  }

  // Validation du formulaire (role et website supprimés)
  private validateForm(): { valid: boolean; error?: string } {
    if (!this.sousBien.name || this.sousBien.name.trim() === '') {
      return { valid: false, error: "Le nom est obligatoire." };
    }

    if (!this.sousBien.description || this.sousBien.description.trim() === '') {
      return { valid: false, error: "La description est obligatoire." };
    }

    if (!this.uploadedSingleImageUrl) {
      return { valid: false, error: "L'image principale est obligatoire." };
    }

    return { valid: true };
  }

  // Soumission du formulaire
  onSubmit(): void {
    const validation = this.validateForm();
    
    if (!validation.valid) {
      Swal.fire({
        icon: "warning",
        title: "Informations manquantes",
        text: validation.error,
        confirmButtonColor: '#f44336'
      });
      return;
    }

    // Préparer les données
    this.sousBien.imagesList = this.uploadedImagesList;
    this.sousBien.image_url = this.uploadedSingleImageUrl;

    this.sousBiensadd = {
      name: this.sousBien.name.trim(),
      category: this.sousBien.category || '',
      description: this.sousBien.description.trim(),
      imagesList: this.sousBien.imagesList,
      image_url: this.sousBien.image_url,
      prix: this.sousBien.prix || ''
      // website et role supprimés
    };

    console.log("Sending data to backend:", this.sousBiensadd);

    this.isSubmitting = true;

    Swal.fire({
      title: 'Création en cours...',
      text: 'Veuillez patienter',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    this.sousBienService.createSousBien(this.sousBiensadd, this.idBien).subscribe(
      (response) => {
        this.isSubmitting = false;
        Swal.close();
        console.log('SousBien added successfully:', response);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Le sous bien a été créé avec succès!",
          confirmButtonColor: '#4caf50'
        }).then(() => {
          // Navigate to the list of sous biens for this bien
          this.router.navigate(['/list-agence-sous-bien', this.idBien]);
        });
      },
      (error) => {
        this.isSubmitting = false;
        Swal.close();
        console.error('Error adding SousBien:', error);
        
        let errorMessage = 'Échec de la création du sous bien. Veuillez réessayer.';
        
        if (error.error) {
          if (typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        Swal.fire({
          icon: "error",
          title: "Échec de la création",
          text: errorMessage,
          confirmButtonColor: '#f44336'
        });
      }
    );
  }

  // Navigation: cancel
  goBack(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-agence-biens', this.agencyId]);
    } else {
      // Fallback to browser history if agency ID is missing
      this._location.back();
    }
  }
}